import { useSyncExternalStore } from 'react'

import { IS_TOUCH } from '../config/touch'
import { DEBUG_MODE } from '../config/debug'

// ============================================================
// Mini-store condiviso tra il mondo 3D (dentro <Canvas>) e la UI
// 2D (fuori dal Canvas). Nessuna dipendenza esterna: usa
// useSyncExternalStore di React 18.
// ============================================================

function createStore(initialState) {
  let state = initialState
  const listeners = new Set()

  return {
    get: () => state,
    set: (next) => {
      state = typeof next === 'function' ? next(state) : next
      listeners.forEach((l) => l())
    },
    subscribe: (listener) => {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
  }
}

// Hook React per leggere uno store in modo reattivo
export function useStore(store) {
  return useSyncExternalStore(store.subscribe, store.get, store.get)
}

// Prodotto attualmente nel mirino (crosshair) — impostato dal raycaster 3D
export const hoveredProductStore = createStore(null)

// Prodotto aperto nella pagina prodotto (null = pagina chiusa)
export const selectedProductStore = createStore(null)

// Drawer carrello aperto/chiuso
export const cartOpenStore = createStore(false)

// Stato del carrello Shopify (oggetto Cart della Storefront API)
export const cartStore = createStore(null)

// Ricerca prodotti aperta/chiusa (overlay SearchOverlay)
export const searchOpenStore = createStore(false)

// true se una qualsiasi UI 2D è aperta (pausa il raycaster del mirino
// e impedisce al click globale di riagganciare il pointer lock)
export function isUiOpen() {
  return selectedProductStore.get() !== null || cartOpenStore.get() ||
         searchOpenStore.get() || watchOpenStore.get() || cassaInCorso() ||
         gumballInCorso()
}

// ------------------------------------------------------------
// OROLOGIO DA POLSO (tasto Q). Il menu delle "app" vive sul
// quadrante: niente più barra HUD fissa sullo schermo.
// ------------------------------------------------------------
export const watchOpenStore = createStore(false)

// ------------------------------------------------------------
// CASSA: si paga al bancone, non dal carrello.
//   ferma  → niente in corso
//   attesa → il POS sta "elaborando"
//   stampa → la carta sta uscendo
//   fatta  → scontrino fuori, si può pagare
// ------------------------------------------------------------
// RICHIAMO DELLA CASSA (chiesto dall'utente): quando qualcosa parla del
// carrello — la mascotte che ricorda di pagare, o il "Vai in cassa" del
// carrello — il contorno del POS lampeggia forte e in fretta per qualche
// secondo, ANCHE DA LONTANO e a qualunque distanza: serve a dire DOVE si
// paga, e da vicino il contorno c'era gia'.
// Oggetto piatto e non uno store: lo legge il useFrame di Cassa.jsx a ogni
// fotogramma, e non deve far ridisegnare niente a React.
export const richiamoCassa = { da: -1e9 }
export function segnalaCassa() {
  richiamoCassa.da = performance.now()
}

export const cassaStore = createStore('ferma')
export const vicinoCassaStore = createStore(false)
export const cassaInCorso = () => cassaStore.get() !== 'ferma'

// ------------------------------------------------------------
// MACCHINETTA DELLE PALLINE (Gumball.jsx). Una giocata attraversa:
//   ferma → niente in corso
//   gira  → la manopola sta girando, a scatti
//   esce  → lo sportellino si apre e la pallina rotola fuori
//   vola  → la pallina sale davanti agli occhi
//   apre  → si apre in due e compare il premio (pannello DOM)
// Il premio si PESCA ALL'INIZIO del giro, non alla fine: il colore della
// pallina che esce E' il premio, quindi dev'essere noto prima che esca.
// ------------------------------------------------------------
export const gumballStore = createStore('ferma')
export const mirinoGumballStore = createStore(false)
export const premioGumballStore = createStore(null)
export const gumballInCorso = () => gumballStore.get() !== 'ferma'
// Cosa dice il server (api/gumball.js), letto una volta all'avvio:
//   visibile = la macchinetta sta sul bancone. ORA E' SEMPRE VERO: e' un pezzo
//              d'arredamento del negozio, e un bancone con un buco dove
//              dovrebbe esserci la macchinetta e' peggio di una macchinetta
//              spenta. Il campo resta perche' un domani si possa toglierla.
//   funziona = ci si puo' giocare davvero. Spenta la variabile su Vercel, la
//              macchinetta si vede e si mira ma dice "fuori servizio".
//   vera     = le giocate passano dal server e i codici sono veri
//   prossima = da quando si puo' rigiocare (ms), 0 = subito
// Finche' il server non risponde vale il default: in pubblico inerte, in
// ?debug giocabile in prova.
export const statoGumballStore = createStore({
  visibile: true, funziona: DEBUG_MODE, vera: false, prossima: 0,
})
// Una riga da mostrare qualche secondo ("si e' inceppata", "hai gia' giocato")
export const avvisoGumballStore = createStore(null)

// In ?debug gli store sono raggiungibili dalla console: serve a provare
// flussi che altrimenti richiedono un carrello vero (window.__store).
if (DEBUG_MODE && typeof window !== 'undefined') {
  setTimeout(() => {
    window.__store = {
      cassa: cassaStore, vicinoCassa: vicinoCassaStore, carrello: cartStore,
      carrelloAperto: cartOpenStore, orologio: watchOpenStore,
      prodotto: selectedProductStore, ricerca: searchOpenStore,
      gumball: gumballStore, premio: premioGumballStore, statoGumball: statoGumballStore,
      // l'avviso della macchinetta: ora lo dice la mascotte, e da console e'
      // l'unico modo di provare quella strada senza farla inceppare davvero
      avvisoGumball: avvisoGumballStore,
    }
  }, 0)
}

// La tendina dei comandi: era stato locale di HelpOverlay, ma ora si
// apre anche dall'icona disegnata sul quadrante, che vive dentro il
// Canvas e non può toccare lo stato di un componente DOM.
export const helpOpenStore = createStore(false)

// true se il movimento del player va bloccato: col carrello aperto e
// col polso alzato. Con la pagina prodotto si può camminare (la vetrina
// segue la camera).
// L'orologio è qui per tre motivi: leggerlo camminando non ha senso,
// libera i tasti direzionali per scorrere le app, e su touch impedisce
// che il trascinamento faccia insieme ruotare la visuale e scorrere il
// quadrante (TouchLookControls guarda questa stessa funzione).
export function isMovementBlocked() {
  // alla cassa si sta fermi a guardare lo scontrino che esce, e alla
  // macchinetta a guardare la pallina
  return cartOpenStore.get() || watchOpenStore.get() || cassaInCorso() ||
         gumballInCorso()
}

// ------------------------------------------------------------
// Pointer lock: quando una UI 2D si chiude, riaggancia il mouse
// al canvas senza costringere l'utente a ricliccare.
// FUNZIONA solo se chiamata dentro un gesto utente (keydown/click):
// il browser rifiuta requestPointerLock fuori da un gesto — in quel
// caso l'errore viene ignorato e resta il click manuale sul canvas.
// ------------------------------------------------------------
// I PointerLockControls (CameraController) registrano qui la loro lock():
// usare quella garantisce lo stesso percorso del click sul canvas
// (lock diretto su querySelector('canvas') può agganciare il canvas
// sbagliato, es. i grafici di r3f-perf: cursore nascosto ma visuale ferma)
let pointerLockFn = null
export function registerPointerLockFn(fn) {
  pointerLockFn = fn
}

export function relockPointer() {
  if (IS_TOUCH) return // su touch non esiste il pointer lock
  if (document.pointerLockElement) return
  try {
    if (pointerLockFn) {
      pointerLockFn()
    } else {
      document.querySelector('#root canvas')?.requestPointerLock()
    }
  } catch {
    // niente: l'utente ricliccherà sul canvas
  }
}

// ------------------------------------------------------------
// Flusso celeste "prodotto → cassa" (effetto 3D, vedi CartFlux.jsx)
// ------------------------------------------------------------

// Posizioni 3D delle scatole prodotto, registrate da ProductsHandler:
// servono al flusso per sapere da dove partire (chiave = product.id)
const productPositions = new Map()
export function registerProductPosition(id, position) {
  productPositions.set(id, position)
}
export function getProductPosition(id) {
  return productPositions.get(id) || null
}
// Prima posizione registrata (usata dal flusso di debug continuo)
export function getFirstProductPosition() {
  const first = productPositions.values().next()
  return first.done ? null : first.value
}

// ------------------------------------------------------------
// Vetrina 3D (ProductShowcase): la scatola vera dello scaffale
// vola davanti alla camera quando la pagina prodotto è aperta.
// ------------------------------------------------------------

// Group Three.js di ogni scatola prodotto (chiave = product.id),
// registrato da ProductBoxModel
const productObjects = new Map()
export function registerProductObject(id, object) {
  if (object) productObjects.set(id, object)
  else productObjects.delete(id)
}
export function getProductObject(id) {
  return productObjects.get(id) || null
}
// Tutti i group prodotto registrati (per il raycast mirato del mirino)
export function getAllProductObjects() {
  return [...productObjects.values()]
}

// Rotazione data dal trascinamento del mouse sulla vetrina:
// la UI 2D scrive, ProductShowcase legge ogni frame (niente reattività)
export const productSpin = { rx: 0, ry: 0 }

// Joystick virtuale (touch): x/y in -1..1, il Player lo legge ogni
// frame come i tasti WASD. Oltre ~0.9 di spinta = corsa.
export const touchMove = { x: 0, y: 0 }

// Indice del media aperto a schermo intero nella lightbox della
// scheda prodotto (null = chiusa). È uno store condiviso perché la
// lightbox si apre anche cliccando le card del carosello 3D.
export const lightboxStore = createStore(null)

// In ?debug: store esposti in console per test e ispezione
// (es. __momoz.selectedProductStore.set(...), __momoz.productSpin)
if (DEBUG_MODE && typeof window !== 'undefined') {
  window.__momoz = {
    get stores() {
      return { selectedProductStore, hoveredProductStore, cartOpenStore, lightboxStore }
    },
    selectedProductStore,
    hoveredProductStore,
    lightboxStore,
    productSpin,
    productIds: () => [...productObjects.keys()],
    getProductObject,
  }
}

// Posizione corrente del prodotto in vetrina: ProductShowcase la
// scrive ogni frame, il carosello di media (ProductMediaOrbit) la
// legge per orbitargli attorno. Oggetto piatto, niente reattività.
export const showcaseState = { active: false, x: 0, y: 0, z: 0, scala: 1, distanza: 1.6 }

// IL BRACCIO CHE TIENE IL PRODOTTO. Lo prepara il Player (e' roba del suo
// scheletro) ma a muoverlo e' ProductShowcase, che gira DOPO di lui nello
// stesso fotogramma: cosi' la mano va dove il prodotto e' gia' stato messo,
// invece di inseguirlo con un fotogramma di ritardo.
let braccioPresa = null
export const registraBraccio = (b) => { braccioPresa = b }
export const getBraccio = () => braccioPresa

// Pad di rotazione del prodotto (mini-joystick nella nuvoletta):
// x/y in -1..1 mentre si trascina il pomello, ProductShowcase li
// applica come VELOCITÀ di rotazione a ogni frame. 0,0 = fermo.
export const productSpinPad = { x: 0, y: 0 }

// Guardia click: le card del carosello marcano il click appena
// consumato, così il handler globale (che col prodotto in mano
// riaggancia il pointer lock sul click al mondo) non lo scambia
// per un "rientra in FPS"
export const clickGuard = { consumed: false }

// Elemento DOM della "nuvoletta" prodotto: ProductShowcase lo
// posiziona ogni frame proiettando sullo schermo la posizione 3D
// della vetrina (niente re-render React, solo style.transform)
let bubbleEl = null
export function registerBubbleEl(el) {
  bubbleEl = el
}
export function getBubbleEl() {
  return bubbleEl
}

// true quando la query prodotti Shopify è conclusa (ok o errore):
// insieme al progresso degli asset 3D decide quando togliere la
// schermata di caricamento (LoadingScreen)
export const productsReadyStore = createStore(false)

// Evento "aggiunto al carrello": la UI 2D lo emette, CartFlux (nel Canvas)
// lo ascolta e fa partire l'arco di particelle
export const cartFluxStore = createStore(null)
export function emitCartFlux(productId) {
  cartFluxStore.set({ id: Date.now() + Math.random(), productId })
}
